import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";
import { countLines } from "@/lib/text";

// Author-typed text of unknown length, rendered so it can never break the box
// it is in. Used by the changelog feed, the SuperAdmin changelog list, and the
// Support inbox's original-request card.
//
// Each of those had its own copy of this paragraph and they had already
// drifted: some kept the author's line breaks and some did not, some wrapped
// long words and some did not, and only one bounded its height — so a 300-line
// changelog entry or a pasted log file in a support ticket turned its card into
// a page of its own and pushed the rest of the layout off the screen.
//
// WHY THE TEXT IS CLAMPED RATHER THAN LEFT TO GROW
//
// A card has a size in the design; a description does not have a size at all.
// Clamping reconciles those, and the `overflow: hidden` a clamp implies is not
// hiding a layout bug here — nothing is lost, because the full text is one
// click away and the stored content is untouched.
//
// WHY IT CANNOT OVERFLOW SIDEWAYS
//
//   * `min-w-0` / `max-w-full`: a flex or grid child defaults to
//     `min-width: auto`, which refuses to shrink below its content. An
//     unbroken 800-character token or a long URL then widens the box, the box
//     widens its row, and the whole page scrolls sideways. Every ancestor
//     between here and the page needs it too — this element alone cannot fix a
//     parent that will not shrink.
//   * `overflow-wrap: anywhere`: breaks that token wherever it must, and unlike
//     `break-word` it also stops the token inflating this element's min-content
//     width, which is what actually keeps it inside its column.
//   * NOT `break-all`: that would break ordinary words mid-syllable and make
//     normal prose unreadable to solve a problem normal prose does not have.
//   * `whitespace-pre-line`: keeps the author's paragraph breaks. Safe, because
//     the text is rendered as TEXT — React escapes it — and never as HTML.
const CLAMP = {
  1: "line-clamp-1",
  2: "line-clamp-2",
  3: "line-clamp-3",
  4: "line-clamp-4",
  5: "line-clamp-5",
  6: "line-clamp-6",
};

export default function ExpandableText({
  text,
  previewLines = 6,
  expandable = false,
  className,
  moreLabel = "Read more",
  lessLabel = "Show less",
}) {
  const [open, setOpen] = useState(false);
  // `clipped` starts from the line count and is corrected by the DOM: a
  // paragraph of six very long lines fits the clamp by count and still gets
  // cut, and the reader must be told that rather than shown text that simply
  // stops mid-sentence.
  const [clipped, setClipped] = useState(false);

  const body = String(text || "");
  const longByLines = countLines(body) > previewLines;
  const clamp = CLAMP[previewLines] || CLAMP[6];

  const measure = (el) => {
    if (!el || open) return;
    setClipped(el.scrollHeight > el.clientHeight + 1);
  };

  const showToggle = expandable && (longByLines || clipped);

  return (
    <div className="min-w-0 max-w-full">
      <p
        ref={measure}
        className={cn(
          "min-w-0 max-w-full whitespace-pre-line break-words [overflow-wrap:anywhere]",
          !open && clamp,
          className
        )}
      >
        {body}
      </p>
      {showToggle && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="mt-1.5 inline-flex items-center gap-1 rounded text-xs font-semibold text-brand-600 transition-colors hover:text-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 dark:text-brand-300 dark:hover:text-brand-200"
        >
          {open ? lessLabel : moreLabel}
          <ChevronDown
            size={13}
            aria-hidden="true"
            className={cn("transition-transform", open && "rotate-180")}
          />
        </button>
      )}
    </div>
  );
}
